import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import { Autocomplete } from "@msys/ui";
import { useTranslate } from "@tolgee/react";
import { debounce } from "lodash-es";
import React from "react";
import {
  QueryPimSearchSuppliersSortBy,
  SortDirection,
} from "../../../clients/graphqlTypes.js";
import {
  SearchSuppliers__SupplierFragment,
  useSupplierSelect__PimSearchSuppliersLazyQuery,
} from "./SupplierSelect.generated.js";

interface Props {
  label?: string;
  placeholder?: string;
  isSubscribed: boolean;
  required?: boolean;
  disabled?: boolean;
  productSupplierId: string | null;
  onChange: (productSupplier: SearchSuppliers__SupplierFragment | null) => void;
}

export const SupplierSelect = ({
  label,
  placeholder,
  isSubscribed,
  required = false,
  disabled = false,
  productSupplierId,
  onChange,
}: Props) => {
  const { t } = useTranslate(["Product", "Global"]);
  const [inputValue, setInputValue] = React.useState("");
  const { supplierOrganisations, totalCount, debouncedRefetch, loading } =
    useSupplierOrganisations(
      isSubscribed,
      productSupplierId,
      inputValue,
      setInputValue
    );

  return (
    <Autocomplete
      placeholder={placeholder ?? t("Type to search", { ns: "Global" })}
      inputLabel={label ?? t("Supplier", { ns: "Product" })}
      inputValue={inputValue}
      options={[
        ...supplierOrganisations,
        ...(totalCount > supplierOrganisations.length
          ? [
              {
                title: t("Type to see more...", { ns: "Global" }),
                id: "__type_to_see_more__" as const,
              },
            ]
          : []),
      ]}
      getOptionLabel={option => option.title}
      getOptionDisabled={option => option.id === "__type_to_see_more__"}
      onInputChange={(_, value, reason) => {
        if (reason === "input") {
          setInputValue(value);
          debouncedRefetch(value);
          if (productSupplierId) onChange(null);
        }
      }}
      value={
        supplierOrganisations.find(
          supplier => supplier.id === productSupplierId
        ) ?? null
      }
      onChange={value => {
        if (value && "__typename" in value) {
          setInputValue(value.title);
          onChange(value);
        } else if (!value) {
          setInputValue("");
          onChange(null);
        }
      }}
      isOptionEqualToValue={(o, v) => o.id === v.id}
      loading={loading}
      required={required}
      disabled={disabled}
    />
  );
};

const LIMIT = 10;

const SUPPLIER_SORTING: {
  column: QueryPimSearchSuppliersSortBy;
  direction: SortDirection;
}[] = [
  {
    column: "name",
    direction: "asc",
  },
];

function useSupplierOrganisations(
  isSubscribed: boolean,
  productSupplierId: string | null,
  inputValue: string,
  setInputValue: (value: string) => void
) {
  const client = useApolloClient();
  const [fetchQuery, query] = useSupplierSelect__PimSearchSuppliersLazyQuery({
    client,
    variables: {
      isSubscribed,
      limit: LIMIT,
      sorting: SUPPLIER_SORTING,
    },
    fetchPolicy: "network-only",
  });

  const inputValueRef = React.useRef(inputValue);
  inputValueRef.current = inputValue;

  const refetch = React.useCallback(
    async (value: string) => {
      await fetchQuery({
        variables: {
          limit: LIMIT,
          sorting: SUPPLIER_SORTING,
          isSubscribed,
          search: value,
        },
      });
    },
    [fetchQuery, isSubscribed]
  );

  const debouncedRefetch = React.useMemo(
    () => debounce(refetch, 500),
    [refetch]
  );

  React.useEffect(() => {
    (async () => {
      if (productSupplierId) {
        const result = await fetchQuery({
          variables: {
            limit: 1,
            sorting: SUPPLIER_SORTING,
            pimSupplierIds: [productSupplierId],
            isSubscribed,
          },
        });
        const option = getDataOrNull(result.data?.pimSearchSuppliers)
          ?.suppliers[0];
        if (option) setInputValue(option.title);
      } else {
        await fetchQuery({
          variables: {
            limit: LIMIT,
            sorting: SUPPLIER_SORTING,
            search: inputValueRef.current,
            isSubscribed,
          },
        });
      }
    })();
  }, [productSupplierId, isSubscribed, fetchQuery, setInputValue]);

  const supplierOrganisations =
    getDataOrNull((query.data ?? query.previousData)?.pimSearchSuppliers)
      ?.suppliers ?? [];
  const totalCount =
    getDataOrNull((query.data ?? query.previousData)?.pimSearchSuppliers)
      ?.totalCount ?? 0;

  return {
    supplierOrganisations,
    refetch,
    totalCount,
    debouncedRefetch,
    loading:
      query.loading ||
      Boolean(
        inputValue &&
        query?.variables?.search &&
        query?.variables?.search !== inputValue
      ),
  };
}
